import React from 'react';
import PropTypes, {
  arrayOf, bool, shape, func, string, number
} from 'prop-types';
import { Fulfillment, Discontinued } from '@thd-olt-component-react/fulfillment';
import { AddToCart } from '@thd-olt-component-react/add-to-cart';
import { TrackOutOfStock } from '@thd-olt-component-react/email-signup';
import { useConfigService } from '@thd-nucleus/experience-context';
import { isMobile } from '../../util';
import { BuyboxActions } from './BuyboxActions';
import * as helper from '../../BuyboxHelper';
import { BuyboxLiteQuantity } from './BuyboxLiteQuantity';

const BuyboxLite = ({
  itemId,
  dualPath,
  isItemOOS,
  configurableItems,
  unavailable,
  storeId,
  isShowATC,
  covidAlert,
  storeZip,
  isATCDisabled,
  channel,
  zipCode,
  fulfillmentMethod,
  fulfillment,
  availabilityType,
  paypalEligible,
  onFulfillmentChange,
  onDeliveryOptionsZipCodeChange,
  onATCClick,
  bopisHolidayTiming,
  hideInstantCheckout,
  hidePaypalCheckout,
  directData,
  configuratorData,
  quantityLimit,
  currentSelectedFulfillment,
  configurableProductDetails,
  availableQuantity,
  subscription,
  showQuantity,
  quantity,
  storeName,
  fallbackMode,
  customRisMessage,
  virtualAssistance,
  risMessageEligible,
  isRisMessageEnabled,
  isOutOfStockOnline,
  minimumBopisQuantity,
  maximumBopisQuantity,
  minimumOrderQuantity,
  quantityLimitExceed,
  onQuantityChange
}) => {
  const enableSms = useConfigService('enableSmsForOOSnotifications') || false;

  if (showQuantity) {
    return (
      <BuyboxLiteQuantity
        itemId={itemId}
        dualPath={dualPath}
        isItemOOS={isItemOOS}
        unavailable={unavailable}
        covidAlert={covidAlert}
        storeId={storeId}
        storeName={storeName}
        isShowATC={isShowATC}
        storeZip={storeZip}
        isATCDisabled={isATCDisabled}
        channel={channel}
        zipCode={zipCode}
        isOutOfStockOnline={isOutOfStockOnline}
        fulfillmentMethod={fulfillmentMethod}
        fulfillment={fulfillment}
        onFulfillmentChange={onFulfillmentChange}
        onDeliveryOptionsZipCodeChange={onDeliveryOptionsZipCodeChange}
        onATCClick={onATCClick}
        configuratorData={configuratorData}
        bopisHolidayTiming={bopisHolidayTiming}
        hideInstantCheckout={hideInstantCheckout}
        hidePaypalCheckout={hidePaypalCheckout}
        directData={directData}
        quantityLimit={quantityLimit}
        currentSelectedFulfillment={currentSelectedFulfillment}
        availableQuantity={availableQuantity}
        subscription={subscription}
        showQuantity={showQuantity}
        quantity={quantity}
        fallbackMode={fallbackMode}
        paypalEligible={paypalEligible}
        virtualAssistance={virtualAssistance}
        onQuantityChange={onQuantityChange}
        customRisMessage={customRisMessage}
        risMessageEligible={risMessageEligible}
        isRisMessageEnabled={isRisMessageEnabled}
        minimumOrderQuantity={minimumOrderQuantity}
        minimumBopisQuantity={minimumBopisQuantity}
        maximumBopisQuantity={maximumBopisQuantity}
        quantityLimitExceed={quantityLimitExceed}
        configurableProductDetails={configurableProductDetails}
      />
    );
  }

  if (isMobile(channel)) {
    const shippingTemplate = helper.renderShippingFulfillment({ fulfillment, availabilityType });
    const cart = configuratorData?.product?.configuration?.cart;
    return (
      <div
        // eslint-disable-next-line tailwindcss/no-arbitrary-value
        className="sui-grid sui-ml-[-10px] sui-mr-[-10px] sui-w-auto sui-bg-primary sui-mb-1"
        data-component="BuyboxLiteMobile"
      >
        <div className="sui-grid-cols-12">
          {shippingTemplate.showATC && (
            <div className="sui-p-2 sui-overflow-hidden">
              <AddToCart
                cartOptions={cart
                  || helper.getCartOptions({
                    configurableItems,
                    channel,
                    fulfillmentMethod: shippingTemplate.method,
                    alterBrowserHistory: true
                  })}
                cartReqParams={cart
                  || helper.getCartReqParams({
                    itemId,
                    configurableItems,
                    storeZip,
                    fulfillmentMethod: shippingTemplate.method,
                    quantity: 1,
                    zipCode,
                    storeId,
                    fulfillment
                  })}
                onClick={onATCClick}
                disabled={isATCDisabled}
              >
                Add to Cart
              </AddToCart>
            </div>
          )}
          {shippingTemplate.discontinued && (
            <Discontinued twoTileToggle />
          )}
          <div className="sui-p-2 sui-overflow-hidden">
            {shippingTemplate.template}
          </div>
          {shippingTemplate.oos && (
            <TrackOutOfStock itemId={itemId} enableSms={enableSms} />
          )}
        </div>
      </div>
    );
  }

  return (
    // eslint-disable-next-line tailwindcss/no-arbitrary-value
    <div className="sui-grid sui-ml-[-10px] sui-mr-[-10px] sui-w-auto" data-component="BuyboxLite">
      <div className="sui-grid-cols-12">
        <div className="sui-p-2 sui-bg-primary sui-overflow-hidden">
          <div>
            {!unavailable && (
              <div>
                <div>
                  <Fulfillment
                    itemId={itemId}
                    covidAlert={covidAlert}
                    onChange={onFulfillmentChange}
                    onDeliveryZipCodeChange={onDeliveryOptionsZipCodeChange}
                    bopisHolidayTiming={bopisHolidayTiming}
                    directData={directData}
                    isBuyBoxLite
                    configurableProductDetails={configurableProductDetails}
                    currentSelectedFulfillment={currentSelectedFulfillment}
                  />
                </div>
              </div>
            )}
          </div>
          {isShowATC && (
            <>
              {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
              <div className="sui-mt-3 sui-max-w-[278px]">
                <BuyboxActions
                  alterBrowserHistory
                  itemId={itemId}
                  storeId={storeId}
                  storeZip={storeZip}
                  disableATC={isATCDisabled}
                  channel={channel}
                  zipCode={zipCode}
                  fulfillmentMethod={fulfillmentMethod}
                  fulfillment={fulfillment}
                  paypalEligible={paypalEligible}
                  onATCClick={onATCClick}
                  hideQuantity
                  hideInstantCheckout={hideInstantCheckout}
                  hidePaypalCheckout={hidePaypalCheckout}
                  configuratorData={configuratorData}
                  quantityLimit={quantityLimit}
                  availableQuantity={availableQuantity}
                  subscription={subscription}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

BuyboxLite.propTypes = {
  itemId: string,
  unavailable: bool,
  covidAlert: bool,
  storeName: string,
  bopisHolidayTiming: number,
  storeId: string,
  isShowATC: bool,
  storeZip: string,
  isATCDisabled: bool,
  channel: string,
  zipCode: string,
  configurableItems: arrayOf(
    shape({
      itemId: string,
      isAnchor: bool
    })
  ),
  fulfillmentMethod: string,
  fulfillment: shape({
    backordered: bool
  }),
  availabilityType: shape({
    discontinued: bool,
    status: bool,
    buyable: bool,
    type: string
  }),
  paypalEligible: bool,
  onFulfillmentChange: func,
  onDeliveryOptionsZipCodeChange: func,
  onATCClick: func,
  hideInstantCheckout: bool,
  hidePaypalCheckout: bool,
  /* eslint-disable-next-line react/forbid-prop-types */
  directData: PropTypes.objectOf(PropTypes.any),
  // eslint-disable-next-line react/forbid-prop-types
  configuratorData: arrayOf(PropTypes.any),
  quantityLimit: number,
  currentSelectedFulfillment: string,
  configurableProductDetails: PropTypes.shape({
    isSpecialOrder: bool,
    configuratorHideQuantity: bool,
    /* eslint-disable-next-line react/forbid-prop-types */
    configuratorData: PropTypes.objectOf(PropTypes.any),
    isConfigurableBlinds: bool
  }),
  availableQuantity: shape({
    pickup: number,
    delivery: number
  }),
  subscription: shape({
    discountPercentage: string
  }),
  minimumBopisQuantity: number,
  maximumBopisQuantity: number,
  fallbackMode: bool,
  minimumOrderQuantity: number,
  onQuantityChange: func,
  quantity: number,
  dualPath: bool,
  isItemOOS: bool,
  risMessageEligible: bool,
  isRisMessageEnabled: bool,
  virtualAssistance: bool,
  customRisMessage: string,
  isOutOfStockOnline: bool,
  quantityLimitExceed: bool,
  showQuantity: bool
};

BuyboxLite.defaultProps = {
  itemId: null,
  configurableItems: null,
  unavailable: false,
  storeId: null,
  isShowATC: true,
  storeName: null,
  covidAlert: false,
  storeZip: null,
  isATCDisabled: false,
  channel: 'desktop',
  zipCode: null,
  fulfillmentMethod: null,
  fulfillment: null,
  availabilityType: null,
  paypalEligible: false,
  onFulfillmentChange: /* istanbul ignore next */ () => {},
  onDeliveryOptionsZipCodeChange: /* istanbul ignore next */ () => {},
  onATCClick: /* istanbul ignore next */ () => {},
  bopisHolidayTiming: null,
  hideInstantCheckout: false,
  hidePaypalCheckout: false,
  directData: null,
  configuratorData: null,
  quantityLimit: 0,
  currentSelectedFulfillment: '',
  configurableProductDetails: {
    isSpecialOrder: false,
    configuratorHideQuantity: false,
    configuratorData: {},
    isConfigurableBlinds: false
  },
  availableQuantity: null,
  subscription: null,
  minimumBopisQuantity: null,
  maximumBopisQuantity: null,
  quantityLimitExceed: false,
  fallbackMode: false,
  quantity: 1,
  onQuantityChange: () => { },
  isOutOfStockOnline: false,
  minimumOrderQuantity: 1,
  showQuantity: false,
  dualPath: false,
  isItemOOS: false,
  risMessageEligible: false,
  virtualAssistance: false,
  isRisMessageEnabled: false,
  customRisMessage: ''
};

export { BuyboxLite };
